import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { groupByFor, previousRange } from '../../core/rules/revenue'
import { today } from '../../core/format/dates'
import { asPeriod, rangeFor, type PeriodKey } from './salesPeriods'

/**
 * De periode in het adres (?periode=&van=&tot=), gedeeld door Omzet en Inzicht: een link van
 * het ene scherm naar het andere neemt de periode mee. Met de vorige periode om mee te vergelijken.
 */
export function usePeriodParams() {
  const [params, setParams] = useSearchParams()
  const period = asPeriod(params.get('periode'))
  const { from, to } = rangeFor(period, today(), { from: params.get('van'), to: params.get('tot') })
  const groupBy = groupByFor(from, to)
  const previous = useMemo(() => previousRange(from, to), [from, to])

  const setPeriod = useCallback(
    (key: PeriodKey) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('periode', key)
          if (key === 'zelf') {
            // Begin bij wat er nu staat, zodat "zelf kiezen" niet ineens leeg is.
            if (!next.get('van')) next.set('van', from)
            if (!next.get('tot')) next.set('tot', to)
          } else {
            next.delete('van')
            next.delete('tot')
          }
          return next
        },
        { replace: true },
      ),
    [setParams, from, to],
  )

  const setCustom = useCallback(
    (name: 'van' | 'tot', value: string) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('periode', 'zelf')
          if (value) next.set(name, value)
          return next
        },
        { replace: true },
      ),
    [setParams],
  )

  /** De periode als zoekdeel, voor een link naar een ander scherm met dezelfde periode. */
  const query = useMemo(() => {
    const p = new URLSearchParams({ periode: period })
    if (period === 'zelf') {
      p.set('van', from)
      p.set('tot', to)
    }
    return p.toString()
  }, [period, from, to])

  return { period, from, to, groupBy, previous, setPeriod, setCustom, query }
}

export type PeriodState = ReturnType<typeof usePeriodParams>
